import { z } from 'zod';
import { PAYMENT_METHODS } from './schemas.js';

/**
 * Phase 2E — Inventory, Purchasing, Expenses.
 *
 * Shared constants + request schemas. Money is integer centavos; stock
 * quantities are integer whole units (unit of measure carried per item).
 * Purchases and Expenses are intentionally SEPARATE ledgers: a purchase
 * never creates an expense record.
 */

/* ---------- Inventory ---------- */

/** Movement kinds. History is append-only (DB trigger blocks UPDATE/DELETE). */
export const MOVEMENT_TYPES = ['STOCK_IN', 'STOCK_OUT', 'ADJUSTMENT'] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];

export const MOVEMENT_TYPE_LABELS: Record<MovementType, string> = {
  STOCK_IN: 'Stock in',
  STOCK_OUT: 'Stock out',
  ADJUSTMENT: 'Adjustment',
};

/** Suggested item categories — free text is allowed, never hardcoded. */
export const INVENTORY_CATEGORIES = [
  'Detergent',
  'Fabcon',
  'Bleach',
  'Plastic bags',
  'Packaging supplies',
  'Cleaning supplies',
  'Gasoline/Fuel',
  'Other',
] as const;

/** Required reason choices for an ADJUSTMENT movement. */
export const ADJUSTMENT_REASONS = [
  'Physical count correction',
  'Damaged stock',
  'Spoilage',
  'Lost stock',
  'Inventory correction',
  'Other',
] as const;

/** Unit of measure for an item's quantity (whole integer units). */
export const STOCK_UNITS = ['pcs', 'bottles', 'packs', 'boxes', 'kg', 'liters', 'rolls', 'other'] as const;
export type StockUnit = (typeof STOCK_UNITS)[number];

const stockQuantitySchema = z.number().int().min(1).max(1_000_000);

export const createInventoryItemSchema = z.object({
  branchId: z.string().trim().min(1),
  name: z.string().trim().min(1, 'Item name is required').max(80),
  category: z.string().trim().min(1, 'Category is required').max(60),
  unit: z.enum(STOCK_UNITS),
  minQuantity: z.number().int().min(0).max(1_000_000).default(0),
  notes: z.string().trim().max(500).optional(),
});
export type CreateInventoryItemInput = z.infer<typeof createInventoryItemSchema>;

export const updateInventoryItemSchema = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    category: z.string().trim().min(1).max(60).optional(),
    unit: z.enum(STOCK_UNITS).optional(),
    minQuantity: z.number().int().min(0).max(1_000_000).optional(),
    notes: z.string().trim().max(500).nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No changes supplied' });
export type UpdateInventoryItemInput = z.infer<typeof updateInventoryItemSchema>;

/** Manual STOCK_IN / STOCK_OUT. quantity is a positive magnitude. */
export const createMovementSchema = z
  .object({
    movementType: z.enum(['STOCK_IN', 'STOCK_OUT']),
    quantity: stockQuantitySchema,
    reason: z.string().trim().min(1, 'Reason is required').max(200),
    referenceType: z.string().trim().max(40).optional(),
    referenceId: z.string().trim().max(64).optional(),
    referenceNumber: z.string().trim().max(64).optional(),
  });
export type CreateMovementInput = z.infer<typeof createMovementSchema>;

/** Admin-only adjustment: signed quantity, mandated reason. */
export const adjustInventorySchema = z
  .object({
    /** Signed: positive = add stock, negative = remove stock. */
    quantity: z
      .number()
      .int()
      .min(-1_000_000)
      .max(1_000_000)
      .refine((v) => v !== 0, { message: 'Adjustment quantity cannot be zero' }),
    reason: z.enum(ADJUSTMENT_REASONS),
    note: z.string().trim().max(300).optional(),
    referenceNumber: z.string().trim().max(64).optional(),
  });
export type AdjustInventoryInput = z.infer<typeof adjustInventorySchema>;

export const listInventoryQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
  category: z.string().trim().max(60).optional(),
  q: z.string().trim().max(64).optional(),
  /**
   * Pagination. Optional by design: omitting `limit` returns every match
   * (existing consumers, e.g. dropdowns, rely on the full list), while list
   * UIs always ask for an explicit page + offset and render the server total.
   */
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
  lowStock: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .optional()
    .transform((v) => v === true || v === 'true'),
  includeInactive: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .optional()
    .transform((v) => v === true || v === 'true'),
});
export type ListInventoryQuery = z.infer<typeof listInventoryQuerySchema>;

export const listMovementsQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
  movementType: z.enum(MOVEMENT_TYPES).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListMovementsQuery = z.infer<typeof listMovementsQuerySchema>;

/* ---------- Purchasing ---------- */

export const PURCHASE_STATUSES = ['DRAFT', 'POSTED', 'CANCELLED'] as const;
export type PurchaseStatus = (typeof PURCHASE_STATUSES)[number];

export const PURCHASE_STATUS_LABELS: Record<PurchaseStatus, string> = {
  DRAFT: 'Draft',
  POSTED: 'Posted',
  CANCELLED: 'Cancelled',
};

/** Reference type stamped on the STOCK_IN movement created by posting. */
export const PURCHASE_REFERENCE = 'PURCHASE' as const;

const purchaseLineSchema = z.object({
  /** Optional: link the line to an existing inventory item for stock-in. */
  inventoryItemId: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1, 'Item description is required').max(120),
  quantity: stockQuantitySchema,
  unit: z.enum(STOCK_UNITS),
  unitPriceCents: z.number().int().min(0).max(10_000_000),
});

export const createPurchaseSchema = z
  .object({
    branchId: z.string().trim().min(1),
    /** ISO date (yyyy-mm-dd). */
    purchaseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Purchase date is required'),
    supplier: z.string().trim().min(1, 'Supplier is required').max(120),
    paymentMethod: z.enum(PAYMENT_METHODS).optional(),
    receiptNumber: z.string().trim().max(64).optional(),
    notes: z.string().trim().max(500).optional(),
    items: z
      .array(purchaseLineSchema)
      .min(1, 'A purchase needs at least one item')
      .max(50),
  });
export type CreatePurchaseInput = z.infer<typeof createPurchaseSchema>;

export const listPurchasesQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
  status: z.enum(PURCHASE_STATUSES).optional(),
  q: z.string().trim().max(64).optional(),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListPurchasesQuery = z.infer<typeof listPurchasesQuerySchema>;

/* ---------- Expenses ---------- */

/** Suggested expense categories — free text allowed, never hardcoded. */
export const EXPENSE_CATEGORIES = [
  'Electricity',
  'Water',
  'Rent',
  'Internet',
  'Transportation',
  'Gasoline/Fuel',
  'Repairs/Maintenance',
  'Salaries/Wages',
  'Other',
] as const;

export const createExpenseSchema = z.object({
  branchId: z.string().trim().min(1),
  /** ISO date (yyyy-mm-dd). */
  expenseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expense date is required'),
  category: z.string().trim().min(1, 'Category is required').max(60),
  description: z.string().trim().min(1, 'Description is required').max(200),
  /** Integer centavos; must be positive. */
  amountCents: z
    .number()
    .int()
    .min(1, 'Amount must be greater than zero')
    .max(100_000_000),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  payee: z.string().trim().max(120).optional(),
  referenceNumber: z.string().trim().max(64).optional(),
  notes: z.string().trim().max(500).optional(),
});
export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;

export const updateExpenseSchema = z
  .object({
    expenseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    category: z.string().trim().min(1).max(60).optional(),
    description: z.string().trim().min(1).max(200).optional(),
    amountCents: z.number().int().min(1).max(100_000_000).optional(),
    paymentMethod: z.enum(PAYMENT_METHODS).optional(),
    payee: z.string().trim().max(120).optional(),
    referenceNumber: z.string().trim().max(64).optional(),
    notes: z.string().trim().max(500).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No changes supplied' });
export type UpdateExpenseInput = z.infer<typeof updateExpenseSchema>;

export const listExpensesQuerySchema = z.object({
  branch: z.string().trim().min(1).optional(),
  category: z.string().trim().max(60).optional(),
  q: z.string().trim().max(64).optional(),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListExpensesQuery = z.infer<typeof listExpensesQuerySchema>;
